import React from "react"
import {
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton
} from "@mui/material"
import { useTheme } from "@mui/system"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import EditIcon from "@mui/icons-material/Edit"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import AppPagination from "components/StyledComponents/AppPagination"
import TableNoRecords from "components/TableNoRecords"
import { restaurantColumns } from "helpers/constant"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { borderBottom: "none" }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { paddingRight: 0 }
  }
}))

const BoolChip = ({ value, trueLabel = "Yes", falseLabel = "No" }) => (
  <Chip
    label={value ? trueLabel : falseLabel}
    size="small"
    sx={{
      bgcolor: value ? "#1a3a1a" : "#3a1a1a",
      color: value ? "#4caf50" : "#f44336",
      fontFamily: "Inter-Regular",
      fontSize: "11px"
    }}
  />
)

const RestaurantListTable = ({
  rows = [],
  setPage,
  currentPage,
  isLoading,
  onEdit,
  isReorderMode,
  reorderList = [],
  onReorderListChange
}) => {
  const theme = useTheme()
  const itemsPerPage = 12

  // Paginated view (normal mode)
  const totalPages = Math.ceil(rows?.length / itemsPerPage)

  const paginatedRows = rows?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const onDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return
    const items = Array.from(reorderList)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    onReorderListChange(items)
  }

  const renderCells = (row) => (
    <>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {row?.icon_url ? (
            <Avatar src={row.icon_url} sx={{ width: 32, height: 32 }} variant="rounded" />
          ) : (
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: `${theme.palette.aqua.main}20` }}
              variant="rounded">
              <RestaurantIcon fontSize="small" sx={{ color: theme.palette.aqua.main }} />
            </Avatar>
          )}
          <Typography variant="body2Regular">{row.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2Regular">{row.owner_name || "—"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2Regular">{row.location || "—"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2Regular">{row.timezone || "UTC"}</Typography>
      </TableCell>
      <TableCell>
        <BoolChip value={row.lite} />
      </TableCell>
      <TableCell>
        <BoolChip value={row.new_user_journey} />
      </TableCell>
      <TableCell>
        <BoolChip value={row.rewards_enabled} />
      </TableCell>
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        <IconButton
          size="small"
          onClick={() => onEdit(row)}
          sx={{ color: theme.palette.aqua.main }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </>
  )

  const renderDraggableRows = (source) =>
    source.length ? (
      source.map((row, index) => (
        <Draggable key={row.id} draggableId={String(row.id)} index={index}>
          {(draggableProvided, snapshot) => (
            <TableRow
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              sx={{
                display: snapshot.isDragging ? "table" : "table-row",
                backgroundColor: snapshot.isDragging ? "rgba(255, 255, 255, 0.05)" : "inherit",
                "& .MuiTableCell-root": {
                  borderBottom: snapshot.isDragging ? "none" : undefined
                }
              }}>
              <TableCell sx={{ paddingLeft: "24px" }} {...draggableProvided.dragHandleProps}>
                <DragIndicatorIcon
                  sx={{ color: theme.palette.text.secondary, opacity: 0.5, cursor: "grab" }}
                />
              </TableCell>
              {renderCells(row)}
            </TableRow>
          )}
        </Draggable>
      ))
    ) : (
      <TableRow>
        <TableNoRecords
          colSpan={restaurantColumns?.length + 1}
          label={isLoading ? "Loading restaurants..." : "No Restaurants Found"}
        />
      </TableRow>
    )

  const tableHead = (showDragCol) => (
    <TableHead>
      <TableRow>
        {showDragCol && <TableCell style={{ width: 50, paddingLeft: "24px" }} />}
        {restaurantColumns?.map((column) => (
          <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
            <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
              {column.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )

  // Reorder mode — all items, full DnD, no pagination
  if (isReorderMode) {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
            <CustomTable stickyHeader aria-label="reorder table">
              {tableHead(true)}
              <Droppable droppableId="restaurants-reorder">
                {(provided) => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {renderDraggableRows(reorderList)}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </CustomTable>
          </TableContainer>
        </Paper>
      </DragDropContext>
    )
  }

  // Normal paginated mode — no drag
  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader aria-label="sticky table">
            {tableHead(false)}
            <TableBody>
              {paginatedRows.length ? (
                paginatedRows.map((row) => <TableRow key={row.id}>{renderCells(row)}</TableRow>)
              ) : (
                <TableRow>
                  <TableNoRecords
                    colSpan={restaurantColumns?.length}
                    label={isLoading ? "Loading restaurants..." : "No Restaurants Found"}
                  />
                </TableRow>
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Paper>
      {rows?.length > 0 && (
        <AppPagination onPageChange={setPage} totalPages={totalPages} currentPage={currentPage} />
      )}
    </>
  )
}

export default RestaurantListTable
